import { filter, isEmpty, map, pipe, toArray } from '@fxts/core';
import { html } from 'rune-ts';
import { loading, loadingEnd } from '@services/MarppleShop/shared/client/global';
import { Alert } from '@services/MarppleShop/renderApp/components/cells/Alert/Alert.ts';
import { Confirm } from '@services/MarppleShop/renderApp/components/cells/Confirm/Confirm.ts';
import { SeeMoreSettingItem } from '../../../../../../../shared/rune-component/Button/SeeMoreSettingButton/SeeMoreSettingButton.ts';
import {
  deleteApi,
  duplicateApi,
  EditorSaveClientError,
  existTempApi,
  prohibitApi,
  saveApi,
  tempSaveApi,
  toTplApi,
} from '@services/MarppleShopStudio/widget/CreatorProduct/api';
import { checkHasUp } from '@modules/Ups/F/fs';
import { GOODS_TYPE } from '@services/MarppleShop/features/Product/type';
import { ValidateFormErrorKeyTypes } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/helper.ts';
import { ACTIONS, MESSAGES } from '../constants.ts';
import { FormValidator } from './FormValidator.ts';
import { ErrorHandler } from './ErrorHandler.ts';
import klass from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/Form.module.scss';
import { CreatorProductData } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { BaseForm } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/BaseForm.ts';
import { AllProductTypes } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/types.ts';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { navigateTo } from '@services/MarppleShop/shared/app/util.ts';
import { makeEmptyScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';

/**
 * Form 액션과 SeeMore 액션을 통합 관리하는 클래스
 */
export class ActionManager<T extends CreatorProductData = CreatorProductData> {
  constructor(
    private form: BaseForm<T>,
    private validator: FormValidator,
    private errorHandler: ErrorHandler,
  ) {}

  // ==================== Form Actions ====================

  /**
   * 폼 저장 실행
   */
  async executeSave(): Promise<void> {
    try {
      // 1. 기본 모드별 검증 에러 확인
      const requiredErrors = this.validator.validateByRequired();
      if (!isEmpty(requiredErrors.errors)) {
        return this.errorHandler.focusFirstError();
      }

      // 2. 선택적 필드 검증
      const optionalErrors = this.validator.validateOptionalFields();
      if (!isEmpty(optionalErrors)) {
        throw new EditorSaveClientError(optionalErrors);
      }

      // 3. 썸네일 검증
      if (this.validator.validateThumbnails()) {
        throw new EditorSaveClientError([ValidateFormErrorKeyTypes.EMPTY_THUMBNAIL]);
      }

      // 4. 성인 상품 안내
      if (this.validator.validateAdult()) {
        await Alert.open({
          title: ET('mps2::adult::need_verify'),
          message: html`
            <div class="${klass.adult_message} ${shareFont('14_medium')}">
              해당 상품은 성인 인증 전까지 <strong>비공개로 등록</strong>됩니다.<br />인증 후 공개로 전환해
              주세요.
            </div>
          `,
          confirmText: '인증하기',
          whiteSpace: 'initial',
        });
      }

      // 5. 모드별 검증 에러 확인
      const modeErrors = this.validator.validateByMode();
      if (!isEmpty(modeErrors)) {
        throw new EditorSaveClientError(modeErrors);
      }

      // 6. 저장 바디 준비
      const body = await this.prepareSaveBody();

      // 7. API 호출
      loading();
      await saveApi(this.form.store.mode, this.form.store.formData as AllProductTypes, { body });
      loadingEnd();

      this.validator.validateAdult()
        ? navigateTo(`${this.form.data.store_url}/settings/ecommerce_info`, makeEmptyScreenNavigate)
        : window.location.reload();
    } catch (error) {
      loadingEnd();
      this.errorHandler.handleSaveError(error);
    }
  }

  /**
   * 임시 저장 실행
   */
  async executeTempSave(): Promise<unknown> {
    if (!this.form.store.formData.store_id) return;

    try {
      const exist = await existTempApi(this.form.store.formData.store_id);

      if (exist && !(await this.confirmOverwriteTempSave())) {
        return;
      }

      loading();
      const body = await this.prepareSaveBody();
      await tempSaveApi(this.form.store.formData, { body });
      loadingEnd();

      return Alert.open({
        title: MESSAGES.TEMP_SAVE.SUCCESS_TITLE,
        message: MESSAGES.TEMP_SAVE.SUCCESS_MESSAGE,
      });
    } catch (error) {
      loadingEnd();
      this.errorHandler.handleSaveError(error);
    }
  }

  /**
   * 저장 바디 준비
   */
  private async prepareSaveBody(): Promise<Record<string, unknown>> {
    const body: Record<string, unknown> = {
      thumbnails: this.form.thumbnailFormView.getFiles(),
    };

    if (this.form.data.stores_product_storage_id) {
      body.stores_product_storage_id = this.form.data.stores_product_storage_id;
    }

    const createSku = 'is_created_sku' in this.form.data && !this.form.data.is_created_sku;

    if (this.validator.shouldValidateSku(createSku)) {
      const errorKeys = this.validator.validateSku();
      const skuValidationPassed = await this.handleSkuValidation(errorKeys);

      if (!skuValidationPassed) {
        throw new EditorSaveClientError([ValidateFormErrorKeyTypes.CANCELLED_SKU]);
      }

      body.create_sku = skuValidationPassed && !isEmpty(errorKeys);
    }

    return body;
  }

  /**
   * SKU 검증 처리
   */
  private async handleSkuValidation(errorKeys: ValidateFormErrorKeyTypes[]): Promise<boolean> {
    for (const errorKey of errorKeys) {
      if ([ValidateFormErrorKeyTypes.DUPLICATED_NICKNAME].includes(errorKey)) {
        await Alert.open({
          title: MESSAGES.SKU.TITLE,
          message: MESSAGES.SKU.DUPLICATED_NICKNAME,
        });
        return false;
      }

      if (
        [ValidateFormErrorKeyTypes.PUBLIC_SKU, ValidateFormErrorKeyTypes.NOT_ALL_CLEAR_SKU].includes(errorKey)
      ) {
        const confirmed = await Confirm.open({
          title: MESSAGES.SKU.TITLE,
          message: MESSAGES.SKU.PUBLIC_SKU,
          confirmText: MESSAGES.BUTTON.REGISTER,
          cancelText: MESSAGES.BUTTON.CANCEL,
          whiteSpace: 'pre_wrap',
        });
        return !!confirmed;
      }

      if (
        [
          ValidateFormErrorKeyTypes.NOT_PUBLIC_SKU,
          ValidateFormErrorKeyTypes.ALL_CLEAR_SKU_PUBLIC_SKU,
        ].includes(errorKey)
      ) {
        const confirmed = await Confirm.open({
          title: MESSAGES.SKU.TITLE,
          message: MESSAGES.SKU.NOT_PUBLIC_SKU,
          confirmText: MESSAGES.BUTTON.REGISTER,
          cancelText: MESSAGES.BUTTON.CANCEL,
          whiteSpace: 'pre_wrap',
        });
        return !!confirmed;
      }
    }

    return true;
  }

  /**
   * 임시저장 덮어쓰기 확인
   */
  private async confirmOverwriteTempSave(): Promise<unknown> {
    return Confirm.open({
      title: MESSAGES.TEMP_SAVE.TITLE,
      message: MESSAGES.TEMP_SAVE.EXIST_CONFIRM,
      confirmText: MESSAGES.BUTTON.SAVE,
      cancelText: MESSAGES.BUTTON.CANCEL,
      whiteSpace: 'pre_wrap',
    });
  }

  // ==================== SeeMore Actions ====================

  /**
   * 더보기 설정 아이템 생성
   */
  createSeeMoreItems(): SeeMoreSettingItem[] {
    return pipe(
      [
        this.createDeleteAction(),
        this.createDuplicateAction(),
        this.createProhibitAction(),
        this.createToTplAction(),
      ],
      filter((state) => state.condition),
      map((state) => state.action()),
      toArray,
    );
  }

  /**
   * 삭제 액션 생성
   */
  private createDeleteAction(): { action: () => SeeMoreSettingItem; condition: boolean } {
    return {
      action: () => ({
        key: ACTIONS.DELETE.key,
        label: ACTIONS.DELETE.name,
        font_klass: klass.see_more_setting_button_font,
        onClick: async () => {
          const { store_id, product_id } = this.form.store.formData;

          if (!store_id || !product_id) return;

          if (await checkHasUp(product_id)) {
            return Alert.open({
              title: MESSAGES.DELETE.HAS_UP_TITLE,
              message: MESSAGES.DELETE.HAS_UP_MESSAGE,
            });
          }

          if (
            !(await Confirm.open({
              title: MESSAGES.DELETE.TITLE,
              message: MESSAGES.DELETE.CONFIRM,
              confirmText: MESSAGES.BUTTON.DELETE,
              cancelText: MESSAGES.BUTTON.CANCEL,
            }))
          )
            return;

          deleteApi(store_id, product_id)
            .then(() => {
              window.location.reload();
            })
            .catch((error) => {
              console.error(error);
              Alert.open({
                title: ACTIONS.DELETE.name,
                message: MESSAGES.DELETE.FAILED,
              });
            });
        },
      }),
      condition: Boolean(this.form.actions.has_permission_to_delete),
    };
  }

  /**
   * 복사 액션 생성
   */
  private createDuplicateAction(): { action: () => SeeMoreSettingItem; condition: boolean } {
    return {
      action: () => ({
        key: ACTIONS.DUPLICATE.key,
        label: ACTIONS.DUPLICATE.name,
        onClick: () => {
          const { store_id, id } = this.form.store.formData;

          if (!store_id || !id) return;

          duplicateApi(store_id, id)
            .then(() => {
              window.location.reload();
            })
            .catch((error) => {
              if (error?.response.status == 403) {
                return Alert.open({
                  title: ACTIONS.DUPLICATE.name,
                  message: MESSAGES.DUPLICATE.FORBIDDEN,
                });
              }

              return Alert.open({
                title: ACTIONS.DUPLICATE.name,
                message: error.response.data.message,
              });
            });
        },
      }),
      condition: Boolean(this.form.actions.has_permission_to_delete),
    };
  }

  /**
   * 판매 보류 액션 생성
   */
  private createProhibitAction(): { action: () => SeeMoreSettingItem; condition: boolean } {
    return {
      action: () => ({
        key: ACTIONS.PROHIBIT.key,
        label: this.form.store.formData.is_prohibited ? '판매 보류 취소' : '판매 보류',
        onClick: async () => {
          const { store_id, id, is_prohibited } = this.form.store.formData;

          if (!store_id || !id) return;

          const title = is_prohibited ? MESSAGES.PROHIBIT.CANCEL_TITLE : MESSAGES.PROHIBIT.TITLE;
          const message = is_prohibited ? MESSAGES.PROHIBIT.CANCEL_CONFIRM : MESSAGES.PROHIBIT.CONFIRM;
          const confirmText = is_prohibited ? MESSAGES.BUTTON.PROHIBIT_CANCEL : MESSAGES.BUTTON.PROHIBIT;

          if (
            !(await Confirm.open({
              title,
              message,
              confirmText,
              cancelText: MESSAGES.BUTTON.CANCEL,
              whiteSpace: 'pre_wrap',
            }))
          )
            return;

          prohibitApi(store_id, id, !is_prohibited)
            .then(() => {
              window.location.reload();
            })
            .catch(() =>
              Alert.open({
                title: ACTIONS.PROHIBIT.name,
                message: MESSAGES.PROHIBIT.FAILED,
              }),
            );
        },
      }),
      condition: Boolean(this.form.actions.has_permission_to_prohibit),
    };
  }

  /**
   * 입고 상품 전환 액션 생성
   */
  private createToTplAction(): { action: () => SeeMoreSettingItem; condition: boolean } {
    return {
      action: () => ({
        key: ACTIONS.TO_TPL.key,
        label: ACTIONS.TO_TPL.name,
        onClick: async () => {
          const { store_id, product_id } = this.form.store.formData;

          if (!store_id || !product_id) return;

          if (
            !(await Confirm.open({
              title: MESSAGES.TO_TPL.TITLE,
              message: MESSAGES.TO_TPL.CONFIRM,
              confirmText: MESSAGES.BUTTON.CONFIRM,
              cancelText: MESSAGES.BUTTON.CANCEL,
            }))
          )
            return;

          toTplApi(store_id, product_id, 0)
            .then(() => {
              window.location.reload();
            })
            .catch((error) => {
              if (error.response.status == 403) {
                return Alert.open({
                  title: MESSAGES.TO_TPL.TITLE,
                  message: MESSAGES.TO_TPL.FORBIDDEN,
                });
              }

              return Alert.open({
                title: MESSAGES.TO_TPL.FAILED_TITLE,
                message: error.response.data.message,
              });
            });
        },
      }),
      condition:
        this.form.data.goods_type_id === GOODS_TYPE.PBG && Boolean(this.form.actions.has_permission_tpl),
    };
  }
}
